import { promises as fs } from 'fs';
import path from 'path';

import { buildProcessToolConfig } from '../../commons/media-tools.config';
import MediaToolsPageComponent, {
    generateMediaToolsProps,
    MediaToolsPageProps,
} from '../../components/media-tools/media-tools-page.component';

interface PageProps extends MediaToolsPageProps {
    url: string;
}

async function loadSelectorOptions() {
    const selectorPath = path.join(process.cwd(), 'public/selector-options/selector-options.json');
    const contents = await fs.readFile(selectorPath, 'utf8');
    return JSON.parse(contents);
}

/**
 * /tools/[tools] renders one page per *process type* (convert, compress, …),
 * listing every tool of that process across all media types. Routes and content
 * are derived from selector-options.json (the xconfig V2 source) via
 * generateMediaToolsProps('all-tools'), so a process type with no tools (e.g.
 * meme) simply gets no page.
 */
// A section carrying `titleOverride` (e.g. FORMAT_HUB) is a synthetic, curated
// section injected by generateMediaToolsProps to surface content inline on the
// category pages (image-tools, video-tools, ...) and the footer — it isn't a
// real ProcessType from selector-options.json and has no display config or SSR
// SEO data. It must never become its own /tools/[tools] route (that would ship
// a thin, mistitled page duplicating content already curated elsewhere).
export async function getStaticPaths() {
    const selectorOptions = await loadSelectorOptions();
    const all = generateMediaToolsProps('all-tools', selectorOptions);
    const paths = (all?.sections ?? [])
        .filter(section => !section.titleOverride)
        .map(section => ({
            params: { tools: section.processType.toLowerCase() },
        }));
    return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
    const selectorOptions = await loadSelectorOptions();
    const all = generateMediaToolsProps('all-tools', selectorOptions);
    if (!all) {
        return { notFound: true };
    }

    const processType = String(params.tools).toUpperCase();
    const section = all.sections.find(s => s.processType === processType && !s.titleOverride);
    if (!section) {
        return { notFound: true };
    }

    const config = buildProcessToolConfig(processType, section.icon);

    // Per-type SSR data carries the authored SEO (seoHeaderTags, page_seo,
    // structured data) plus toolsDropdownData/environment — same convention as
    // the hub pages (image-tools etc.). Generated by xconvert-web-ssr from its
    // common.routers.ts `tools/<process>` entries. Falls back gracefully to the
    // synthesized config if no per-type file exists for this process type.
    let ssrProps = {};
    try {
        const ssrDataPath = path.join(process.cwd(), `ssr-data/build/tools/${params.tools}.json`);
        ssrProps = JSON.parse(await fs.readFile(ssrDataPath, 'utf8'));
    } catch (e) {
        // No per-type SSR data — page still renders with synthesized SEO.
    }

    return {
        props: {
            ...ssrProps,
            config,
            sections: [section],
            url: `/tools/${params.tools}`,
        },
    };
}

export default function ProcessToolsPage(props: PageProps) {
    return <MediaToolsPageComponent {...props} />;
}
